<template>
  <div id="backdoor_mock_sound">
    <div class="main">
      <div class="title_bar">
        <div class="left">
          <div class="title">
            音效通知測試
          </div>
          <div class="tip">
            在本分頁模擬 Service Worker 推播（不經後端），測試告警鈴聲與點餐通知音效的播放規則。規則定義見 CONTEXT.md「音效通知」
          </div>
        </div>
      </div>

      <el-alert
        v-if="!isServiceWorkerSupported"
        class="alert"
        type="error"
        :closable="false"
        title="此瀏覽器環境沒有 navigator.serviceWorker（需 https 或 localhost），無法模擬推播"
      />
      <el-alert
        v-if="autoRunHint"
        class="alert"
        type="warning"
        :closable="false"
        :title="autoRunHint"
      />

      <div class="layout">
        <div class="control_panel">
          <div class="section_title">
            觸發延遲
          </div>
          <div class="delay_row">
            <el-input-number
              v-model="delaySeconds"
              :min="0"
              :max="60"
              size="small"
            />
            <span>秒後觸發（用於切換分頁，測試非主聲分頁與失焦）</span>
          </div>
          <div v-if="countdown > 0" class="countdown">
            {{ countdown }} 秒後觸發「{{ countdownLabel }}」
          </div>

          <div class="section_title">
            情境
          </div>
          <el-table
            :data="scenarios"
            size="small"
            class="scenario_table"
          >
            <el-table-column label="情境" min-width="150">
              <template slot-scope="scope">
                <div class="scenario_title">
                  {{ scope.row.title }}
                </div>
                <div class="scenario_timeline">
                  {{ formatSteps(scope.row.steps) }}
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="expected" label="預期" min-width="160" />
            <el-table-column label="" width="150">
              <template slot-scope="scope">
                <el-button size="mini" type="primary" plain @click="runScenario(scope.row)">
                  觸發
                </el-button>
                <el-button size="mini" @click="copyAutoRunUrl(scope.row)">
                  複製 autoRun
                </el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="section_title">
            自訂觸發
          </div>
          <div class="custom_row">
            <el-select v-model="custom.type" size="small" class="custom_type">
              <el-option
                v-for="(label, type) in EVENT_LABEL"
                :key="type"
                :label="label"
                :value="type"
              />
            </el-select>
            <el-input-number
              v-model="custom.count"
              :min="1"
              :max="20"
              size="small"
            />
            <span>筆，間隔</span>
            <el-input-number
              v-model="custom.interval"
              :min="0"
              :max="30000"
              :step="100"
              size="small"
            />
            <span>毫秒</span>
            <el-button size="small" type="primary" @click="runCustom">
              觸發
            </el-button>
          </div>

          <div class="section_title">
            自動播放被擋（autoRun）
          </div>
          <ol class="guide">
            <li>在上方情境按「複製 autoRun」</li>
            <li>開新分頁貼上網址，<b>不要點擊頁面</b>，等待倒數結束</li>
            <li>預期：不發聲、畫面無錯誤、跳出音效權限教學 Dialog</li>
            <li>若仍有聲音，是 Chrome 依網站互動紀錄放行自動播放，可用 <code>--autoplay-policy=document-user-activation-required</code> 啟動 Chrome 再測</li>
          </ol>
        </div>

        <div class="status_panel">
          <div class="section_title">
            目前狀態
          </div>
          <div class="state_grid">
            <div class="state_item">
              <span class="state_item__label">主聲分頁</span>
              <el-tag size="small" :type="state.isLastFocusTab ? 'success' : 'info'">
                {{ state.isLastFocusTab ? '是' : '否' }}
              </el-tag>
            </div>
            <div class="state_item">
              <span class="state_item__label">告警鈴聲</span>
              <el-tag size="small" :type="state.sirenLevel ? 'danger' : 'info'">
                {{ state.sirenLevel ? `L${state.sirenLevel} 播放中` : '無' }}
              </el-tag>
            </div>
            <div class="state_item">
              <span class="state_item__label">點餐通知音效</span>
              <el-tag size="small" :type="state.isOrderPlaying ? 'warning' : 'info'">
                {{ state.isOrderPlaying ? '播放中' : '無' }}
              </el-tag>
            </div>
            <div class="state_item">
              <span class="state_item__label">待播</span>
              <el-tag size="small" :type="state.hasPendingOrderNotify ? 'warning' : 'info'">
                {{ state.hasPendingOrderNotify ? '有' : '無' }}
              </el-tag>
            </div>
          </div>
          <div class="button_row">
            <el-button size="small" type="danger" @click="stopAll">
              停止全部
            </el-button>
            <el-button size="small" @click="logs = []">
              清除紀錄
            </el-button>
          </div>

          <div class="section_title">
            時間軸（時間為距離觸發開始的秒數）
          </div>
          <pre class="log_panel">{{ logText }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DiningOption } from '@/js/utils/orderFood'
import * as audioManager from '@/js/utils/audioManager'

const EVENT_TYPE = {
  NEW_ORDER: 'newOrder',
  CANCEL_ORDER: 'cancelOrder',
  SIREN_L3: 'sirenL3',
  SIREN_L4: 'sirenL4'
}

const EVENT_LABEL = {
  [EVENT_TYPE.NEW_ORDER]: '新訂單',
  [EVENT_TYPE.CANCEL_ORDER]: '取消訂單',
  [EVENT_TYPE.SIREN_L3]: '私區告警 L3',
  [EVENT_TYPE.SIREN_L4]: '私區告警 L4'
}

const ORDER_NOTIFICATION_TYPE_ID = {
  [EVENT_TYPE.NEW_ORDER]: 4, // 新訂單
  [EVENT_TYPE.CANCEL_ORDER]: 14 // 取消訂單
}

const STATE_POLLING_INTERVAL = 100 // 狀態輪詢間隔（毫秒）

/**
 * 情境清單，steps.at 為距離觸發開始的毫秒數
 */
const SCENARIOS = [
  {
    key: 'singleNewOrder',
    title: '單筆新訂單',
    expected: '播 3 次，間隔 0.5 秒',
    steps: [{ at: 0, type: EVENT_TYPE.NEW_ORDER }]
  },
  {
    key: 'singleCancelOrder',
    title: '單筆取消訂單',
    expected: '播 3 次，間隔 0.5 秒',
    steps: [{ at: 0, type: EVENT_TYPE.CANCEL_ORDER }]
  },
  {
    key: 'burstOrders',
    title: '同時 3 筆新訂單',
    expected: '只播一輪（3 次）',
    steps: [
      { at: 0, type: EVENT_TYPE.NEW_ORDER },
      { at: 100, type: EVENT_TYPE.NEW_ORDER },
      { at: 200, type: EVENT_TYPE.NEW_ORDER }
    ]
  },
  {
    key: 'ordersDuringRound',
    title: '一輪中陸續到達',
    expected: '只播一輪（3 次），播完不補播',
    steps: [
      { at: 0, type: EVENT_TYPE.NEW_ORDER },
      { at: 3000, type: EVENT_TYPE.CANCEL_ORDER }
    ]
  },
  {
    key: 'orderDuringSiren',
    title: '告警中到達訂單',
    expected: '告警 6 秒結束後補播一輪',
    steps: [
      { at: 0, type: EVENT_TYPE.SIREN_L3 },
      { at: 1000, type: EVENT_TYPE.NEW_ORDER },
      { at: 2000, type: EVENT_TYPE.NEW_ORDER }
    ]
  },
  {
    key: 'sirenDuringOrder',
    title: '點餐中來告警',
    expected: '點餐音效立刻中斷，告警結束後不補播',
    steps: [
      { at: 0, type: EVENT_TYPE.NEW_ORDER },
      { at: 3000, type: EVENT_TYPE.SIREN_L4 }
    ]
  },
  {
    key: 'sirenUpgrade',
    title: '告警升級',
    expected: 'L3 被 L4 蓋掉，L4 結束後才補播一輪',
    steps: [
      { at: 0, type: EVENT_TYPE.SIREN_L3 },
      { at: 1000, type: EVENT_TYPE.NEW_ORDER },
      { at: 2000, type: EVENT_TYPE.SIREN_L4 }
    ]
  }
]

/**
 * 取得音效檔名（移除 webpack hash）
 * @param {string} src - 音效網址
 * @returns {string}
 */
const getAudioName = (src) => {
  const fileName = (src || '').split('/').pop().split('?')[0]
  return fileName.replace(/\.[0-9a-f]{8}(\.\w+)$/, '$1')
}

export default {
  name: 'BackdoorMockSound',
  data () {
    return {
      EVENT_LABEL,
      scenarios: SCENARIOS,
      isServiceWorkerSupported: 'serviceWorker' in navigator,
      delaySeconds: 0,
      countdown: 0,
      countdownLabel: '',
      custom: {
        type: EVENT_TYPE.NEW_ORDER,
        count: 3,
        interval: 100
      },
      state: audioManager.getState(),
      logs: [],
      autoRunHint: '',
      runStartAt: null,
      /** 已排程的觸發計時器，停止全部時清除 */
      stepTimerIds: [],
      countdownTimerId: null,
      stateTimerId: null,
      originalPlay: null,
      originalPause: null
    }
  },
  computed: {
    logText () {
      if (this.logs.length === 0) return '尚無紀錄'
      return this.logs.map(log => `${log.time}  ${log.text}`).join('\n')
    }
  },
  mounted () {
    this.patchMediaElement()
    this.stateTimerId = setInterval(this.pollState, STATE_POLLING_INTERVAL)
    this.handleAutoRun()
  },
  beforeDestroy () {
    this.clearScheduled()
    clearInterval(this.stateTimerId)
    this.restoreMediaElement()
  },
  methods: {
    /**
     * 攔截 HTMLMediaElement 的 play / pause，記錄每一次實際播放
     */
    patchMediaElement () {
      const vm = this
      const originalPlay = HTMLMediaElement.prototype.play
      const originalPause = HTMLMediaElement.prototype.pause
      this.originalPlay = originalPlay
      this.originalPause = originalPause
      HTMLMediaElement.prototype.play = function () {
        const audioName = getAudioName(this.currentSrc || this.src)
        const playPromise = originalPlay.apply(this, arguments)
        if (playPromise && playPromise.then) {
          playPromise
            .then(() => vm.addLog(`▶ 播放 ${audioName}（音量 ${this.volume}）`))
            .catch(error => vm.addLog(`✖ 播放失敗 ${audioName}：${error.name}`))
        }
        return playPromise
      }
      HTMLMediaElement.prototype.pause = function () {
        if (!this.paused) vm.addLog(`■ 停止 ${getAudioName(this.currentSrc || this.src)}`)
        return originalPause.apply(this, arguments)
      }
    },
    /**
     * 還原 HTMLMediaElement 的 play / pause
     */
    restoreMediaElement () {
      if (this.originalPlay) HTMLMediaElement.prototype.play = this.originalPlay
      if (this.originalPause) HTMLMediaElement.prototype.pause = this.originalPause
    },
    /**
     * 輪詢 audioManager 狀態，狀態變化時寫入時間軸
     */
    pollState () {
      const prev = this.state
      const next = audioManager.getState()
      if (prev.isLastFocusTab !== next.isLastFocusTab) {
        this.addLog(next.isLastFocusTab ? '本分頁成為主聲分頁' : '本分頁不再是主聲分頁')
      }
      if (prev.sirenLevel !== next.sirenLevel) {
        this.addLog(next.sirenLevel ? `告警鈴聲 L${next.sirenLevel} 開始` : '告警鈴聲結束')
      }
      if (prev.isOrderPlaying !== next.isOrderPlaying) {
        this.addLog(next.isOrderPlaying ? '點餐通知音效：一輪開始' : '點餐通知音效：一輪結束')
      }
      if (prev.hasPendingOrderNotify !== next.hasPendingOrderNotify) {
        this.addLog(next.hasPendingOrderNotify ? '留下待播' : '待播清除')
      }
      this.state = next
    },
    /**
     * 寫入時間軸
     * @param {string} text
     */
    addLog (text) {
      const time = this.runStartAt === null
        ? '    -   '
        : `+${((Date.now() - this.runStartAt) / 1000).toFixed(2).padStart(6, ' ')}s`
      this.logs.push({ time, text })
    },
    /**
     * 情境步驟文字
     * @param {Array} steps
     * @returns {string}
     */
    formatSteps (steps) {
      return steps.map(step => `${step.at / 1000}s ${EVENT_LABEL[step.type]}`).join(' → ')
    },
    /**
     * 觸發情境
     * @param {Object} scenario
     */
    runScenario (scenario) {
      this.schedule(scenario.title, scenario.steps)
    },
    /**
     * 觸發自訂事件
     */
    runCustom () {
      const steps = Array.from({ length: this.custom.count }, (_, index) => ({
        at: index * this.custom.interval,
        type: this.custom.type
      }))
      this.schedule(`自訂：${EVENT_LABEL[this.custom.type]} × ${this.custom.count}`, steps)
    },
    /**
     * 依延遲秒數排程觸發
     * @param {string} label - 顯示名稱
     * @param {Array} steps - 觸發步驟
     */
    schedule (label, steps) {
      this.clearScheduled()
      const start = () => {
        this.countdown = 0
        this.runStartAt = Date.now()
        this.logs = []
        this.addLog(`開始：${label}`)
        steps.forEach(step => {
          const timerId = setTimeout(() => this.dispatchMockEvent(step.type), step.at)
          this.stepTimerIds.push(timerId)
        })
      }
      if (this.delaySeconds <= 0) {
        start()
        return
      }
      this.countdown = this.delaySeconds
      this.countdownLabel = label
      this.countdownTimerId = setInterval(() => {
        this.countdown--
        if (this.countdown > 0) return
        clearInterval(this.countdownTimerId)
        this.countdownTimerId = null
        start()
      }, 1000)
    },
    /**
     * 清除所有尚未觸發的排程
     */
    clearScheduled () {
      this.stepTimerIds.forEach(timerId => clearTimeout(timerId))
      this.stepTimerIds = []
      if (this.countdownTimerId) {
        clearInterval(this.countdownTimerId)
        this.countdownTimerId = null
      }
      this.countdown = 0
    },
    /**
     * 停止全部：清除排程並停止所有音效
     */
    stopAll () {
      this.clearScheduled()
      audioManager.stopAll()
      this.addLog('停止全部')
    },
    /**
     * 產生模擬推播資料，格式與 firebase-messaging-sw.js 轉發的 notification 相同
     * @param {string} type - EVENT_TYPE
     * @returns {Object}
     */
    createMockPayload (type) {
      const now = new Date()
      const serial = `${Date.now()}${Math.floor(Math.random() * 1000)}`
      if (type === EVENT_TYPE.SIREN_L3 || type === EVENT_TYPE.SIREN_L4) {
        return {
          FeatureName: 'BA-alert',
          TabletId: 'backdoor-mock-tablet',
          TagLevel: type === EVENT_TYPE.SIREN_L3 ? 3 : 4,
          Id: `backdoor-mock-${serial}`,
          title: '後門模擬私區告警',
          AssetPosition: '後門模擬戶別',
          EventCreatedTime: now.toISOString()
        }
      }
      // ComOrderId 前 8 碼為日期（OrderFoodNotify 以此格式化編號）
      const datePrefix = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`
      return {
        FeatureName: 'communityTableFoodForm',
        title: '後門模擬點餐推播',
        Data: JSON.stringify({
          NotificationTypeId: ORDER_NOTIFICATION_TYPE_ID[type],
          ComOrderId: `${datePrefix}${serial.slice(-6)}`,
          DiningOption: DiningOption.DineIn,
          DiningOptionName: '內用',
          TableNo: 'A1',
          ReservationAtUtc: null
        })
      }
    },
    /**
     * 在本分頁模擬一則 Service Worker 推播
     * @param {string} type - EVENT_TYPE
     */
    dispatchMockEvent (type) {
      if (!this.isServiceWorkerSupported) return
      this.addLog(`→ 推播：${EVENT_LABEL[type]}`)
      navigator.serviceWorker.dispatchEvent(new MessageEvent('message', {
        data: this.createMockPayload(type)
      }))
    },
    /**
     * 複製自動觸發網址（用於測試自動播放被擋）
     * @param {Object} scenario
     */
    async copyAutoRunUrl (scenario) {
      const { href } = this.$router.resolve({
        name: 'backdoorMockSound',
        query: { autoRun: scenario.key, delay: 5 }
      })
      const url = `${window.location.origin}${href}`
      try {
        await navigator.clipboard.writeText(url)
        this.$message({ message: '已複製，請開新分頁貼上，且不要點擊頁面', type: 'success' })
      } catch (error) {
        this.$alert(url, '請手動複製網址').then(() => {}).catch(() => {})
      }
    },
    /**
     * 處理網址參數 autoRun / delay：頁面載入後不經使用者互動自動觸發情境
     */
    handleAutoRun () {
      const { autoRun, delay } = this.$route.query
      if (!autoRun) return
      const scenario = SCENARIOS.find(item => item.key === autoRun)
      if (!scenario) {
        this.autoRunHint = `找不到 autoRun 情境：${autoRun}`
        return
      }
      this.delaySeconds = Math.max(parseInt(delay) || 0, 0)
      this.autoRunHint = `autoRun：${scenario.title}。倒數期間不要點擊頁面，以模擬未互動過的分頁`
      this.runScenario(scenario)
    }
  }
}
</script>

<style lang="scss" scoped>
#backdoor_mock_sound {
  @include mainLayer();
  .title {
    font-weight: bold;
    font-size: 25px;
    margin-bottom: 10px;
    text-align: left;
  }
  .tip {
    text-align: left;
    color: #777;
  }
  .alert {
    margin-top: 16px;
  }
  .layout {
    display: flex;
    gap: 32px;
    align-items: flex-start;
    margin-top: 20px;
    text-align: left;
  }
  .control_panel {
    flex: 1 1 0;
    min-width: 0;
  }
  .status_panel {
    flex: 0 0 420px;
  }
  .section_title {
    font-weight: bold;
    margin: 20px 0 8px;
    padding-left: 8px;
    border-left: 4px solid #333;
  }
  .delay_row,
  .custom_row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
  }
  .custom_type {
    width: 140px;
  }
  .countdown {
    margin-top: 8px;
    color: #e6a23c;
    font-weight: bold;
  }
  .scenario_title {
    font-weight: bold;
  }
  .scenario_timeline {
    font-size: 12px;
    color: #888;
  }
  .scenario_table ::v-deep .el-button + .el-button {
    margin-left: 0;
    margin-top: 4px;
  }
  .guide {
    padding-left: 20px;
    line-height: 1.8;
    li {
      list-style: decimal;
    }
    code {
      font-family: Menlo, Consolas, monospace;
      font-size: 12px;
      background: #f5f5f5;
      padding: 0 4px;
    }
  }
  .state_grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
  .state_item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #f5f5f5;
    border-radius: 4px;
    &__label {
      color: #555;
    }
  }
  .button_row {
    display: flex;
    gap: 8px;
    margin-top: 12px;
    .el-button + .el-button {
      margin-left: 0;
    }
  }
  .log_panel {
    height: 420px;
    overflow: auto;
    margin: 0;
    padding: 12px;
    background: #1e1e1e;
    color: #d4d4d4;
    font-family: Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    border-radius: 4px;
  }
}
</style>
